import { LateChip } from '@/components/entry/LateChip';
import { BundleApply } from '@/components/entry/BundleApply';
import { useApplicationBundles } from '@/hooks/useApplicationBundles';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useBlocker } from '@tanstack/react-router';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { cropDayLabel, cropPhase, type Crop } from '@/lib/crop';
import { todayInPH } from '@/lib/phDate';
import { parseTypedAmount } from '@/lib/number';
import { bigAmountLimit } from '@/lib/applicationAmount';
import type { ApplicationResponse, FarmProductResponse } from '@/types/generated/openapi';
import { EmptyNote, Kicker, Section } from './shared';
import { amountText, CATEGORY_LABEL, categoryLabel } from './format';
import { qk } from '@/lib/queryKeys';
import { useEnteredBy } from '@/hooks/useEnteredBy';
import { useMembership } from '@/hooks/useMembership';
import { useAuth } from '@/hooks/useAuth';
import { canEditApplication, wasEdited } from '@/lib/applicationEdit';
import { useIdempotencyKey } from '@/lib/idempotency';
import { ApplicationEditDialog } from './ApplicationEditDialog';
import {
  useApplicationJournal,
  useApplicationSummary,
  useFarmProducts,
  useReadingReasons,
  type JournalPhase,
} from './data';

interface ApplicationsTabProps {
  farmId: string;
  crop: Crop;
  /** The pond's area in hectares: the default for a bundle's area box. */
  pondAreaHa?: number | string | null;
}

export function ApplicationsTab({ farmId, crop, pondAreaHa }: ApplicationsTabProps) {
  const open = cropPhase(crop) !== 'closed';
  return (
    <div className="flex flex-wrap items-start gap-5">
      <Journal farmId={farmId} crop={crop} />
      <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-5 max-md:order-first">
        {open && <LogApplicationForm farmId={farmId} crop={crop} />}
        {open && <BundleSection farmId={farmId} crop={crop} pondAreaHa={pondAreaHa} />}
        <CropTotals farmId={farmId} crop={crop} />
      </div>
    </div>
  );
}

type PhaseFilter = 'all' | JournalPhase;

function FilterChip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        'min-h-[32px] rounded-full border px-3 text-[13px] font-medium',
        on ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
      )}
    >
      {children}
    </button>
  );
}

function groupByDate(rows: ApplicationResponse[]): Array<[string, ApplicationResponse[]]> {
  const m = new Map<string, ApplicationResponse[]>();
  for (const r of rows) {
    const list = m.get(r.application_date) ?? [];
    list.push(r);
    m.set(r.application_date, list);
  }
  return [...m.entries()];
}

function Journal({ farmId, crop }: { farmId: string; crop: Crop }) {
  const stocked = !!crop.stocking_date;
  const [phase, setPhase] = useState<PhaseFilter>('all');
  const [category, setCategory] = useState<string | undefined>();
  const [prepOpen, setPrepOpen] = useState(false);
  const { data: summary } = useApplicationSummary(farmId, crop.id);

  // Whole crop on a stocked crop: grow-out on top, prep folded at the bottom.
  const foldPrep = stocked && phase === 'all';
  const mainPhase: JournalPhase | undefined = foldPrep ? 'grow_out' : phase === 'all' ? undefined : phase;
  const main = useApplicationJournal(farmId, crop.id, { phase: mainPhase, category });
  const prep = useApplicationJournal(farmId, crop.id, { phase: 'prep', category }, { enabled: foldPrep && prepOpen });

  const rows = main.data?.pages.flatMap((p) => p.data) ?? [];
  const prepCount = category ? null : (summary?.prep_entries ?? null);
  const prepRange =
    crop.prep_start_date && crop.stocking_date
      ? `${format(parseISO(crop.prep_start_date), 'MMM d')} – ${format(parseISO(crop.stocking_date), 'MMM d')}`
      : null;

  return (
    <Section labelledBy="ap-journal" title="Application journal" className="flex-[2_1_560px]">
      <div className="-mt-1 flex flex-col gap-2">
        {stocked && (
          <div role="group" className="flex flex-wrap gap-1.5" aria-label="Crop phase">
            <FilterChip on={phase === 'all'} onClick={() => setPhase('all')}>Whole crop</FilterChip>
            <FilterChip on={phase === 'prep'} onClick={() => setPhase('prep')}>Pre-stocking</FilterChip>
            <FilterChip on={phase === 'grow_out'} onClick={() => setPhase('grow_out')}>Grow-out</FilterChip>
          </div>
        )}
        <div role="group" className="flex flex-wrap gap-1.5" aria-label="Product category">
          <FilterChip on={!category} onClick={() => setCategory(undefined)}>All products</FilterChip>
          {Object.entries(CATEGORY_LABEL).map(([value, label]) => (
            <FilterChip key={value} on={category === value} onClick={() => setCategory(value)}>
              {label}
            </FilterChip>
          ))}
        </div>
      </div>

      {main.isLoading ? (
        <EmptyNote>Loading…</EmptyNote>
      ) : rows.length === 0 && !foldPrep ? (
        <EmptyNote>No applications{category ? ` in ${categoryLabel(category).toLowerCase()}` : ''} for this crop yet.</EmptyNote>
      ) : (
        <JournalDays farmId={farmId} crop={crop} rows={rows} />
      )}
      {foldPrep && rows.length === 0 && !main.isLoading && <EmptyNote>No grow-out applications yet.</EmptyNote>}

      {main.hasNextPage && (
        <div className="flex items-center justify-between border-b border-gray-200 py-2.5 text-sm text-gray-500">
          <span>Older entries</span>
          <button
            type="button"
            onClick={() => main.fetchNextPage()}
            disabled={main.isFetchingNextPage}
            className="min-h-[32px] font-semibold text-primary-600 hover:text-primary-700"
          >
            {main.isFetchingNextPage ? 'Loading…' : 'Show more'}
          </button>
        </div>
      )}

      {foldPrep && (prepCount === null || prepCount > 0) && (
        <div className="mt-1 rounded-lg bg-blue-50 px-3 py-2.5">
          <button
            type="button"
            aria-expanded={prepOpen}
            onClick={() => setPrepOpen((o) => !o)}
            className="flex min-h-[32px] w-full flex-wrap items-baseline justify-between gap-2 text-left text-sm"
          >
            <b className="text-blue-900">Pre-stocking{prepRange ? ` · ${prepRange}` : ''}</b>
            <span className="font-semibold text-primary-600">
              {prepOpen ? 'Hide' : prepCount !== null ? `${prepCount} ${prepCount === 1 ? 'entry' : 'entries'} · show` : 'Show'}
            </span>
          </button>
          {prepOpen && (
            <div className="mt-2">
              {prep.isLoading ? (
                <EmptyNote>Loading…</EmptyNote>
              ) : (
                <JournalDays farmId={farmId} crop={crop} rows={prep.data?.pages.flatMap((p) => p.data) ?? []} />
              )}
              {prep.hasNextPage && (
                <button
                  type="button"
                  onClick={() => prep.fetchNextPage()}
                  className="min-h-[32px] text-sm font-semibold text-primary-600"
                >
                  Show more
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

function JournalDays({ farmId, crop, rows }: { farmId: string; crop: Crop; rows: ApplicationResponse[] }) {
  const today = todayInPH();
  const enteredBy = useEnteredBy(farmId);
  const { user } = useAuth();
  const { membership, isSupervisorOrAbove } = useMembership();
  const [editing, setEditing] = useState<{ row: ApplicationResponse; deleting: boolean } | null>(null);
  const cropOpen = cropPhase(crop) !== 'closed';
  const canEdit = (row: ApplicationResponse) =>
    canEditApplication(
      { isSupervisorOrAbove, membersEdit: !!membership?.membersEditApplications, cropOpen, userId: user?.id },
      row
    );
  return (
    <div className="flex flex-col">
      {editing && (
        <ApplicationEditDialog
          farmId={farmId}
          cropId={crop.id}
          row={editing.row}
          canDelete={isSupervisorOrAbove}
          canSeeHistory={isSupervisorOrAbove}
          startWithDelete={editing.deleting}
          onClose={() => setEditing(null)}
        />
      )}
      {groupByDate(rows).map(([date, entries]) => (
        <div key={date}>
          <div className="flex items-baseline justify-between border-b border-gray-200 pb-1.5 pt-2.5 text-[13px]">
            <b>
              {[cropDayLabel(crop, date), date === today ? `Today, ${format(parseISO(date), 'MMM d')}` : format(parseISO(date), 'MMM d')]
                .filter(Boolean)
                .join(' · ')}
            </b>
            <span className="text-gray-500">
              {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
            </span>
          </div>
          {entries.map((e) => {
            const entered = enteredBy(e, e.application_date);
            return (
              <div key={e.id} className="flex items-start justify-between gap-3 border-b border-gray-100 py-2.5 text-sm last:border-0">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <b className="text-gray-900">{e.product_name || 'Unnamed'}</b>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
                      {categoryLabel(e.product_category)}
                    </span>
                    {e.bundle_name && (
                      <span className="rounded-full bg-primary-50 px-2 py-0.5 text-xs font-semibold text-primary-700" data-bundle-chip>
                        {e.bundle_name}
                      </span>
                    )}
                    {wasEdited(e) && (
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800" data-edited-badge>
                        Edited
                      </span>
                    )}
                    <LateChip row={e} entryDate={e.application_date} />
                  </div>
                  {(e.reason || e.notes) && (
                    <p className="mt-0.5 text-gray-600">{[e.reason, e.notes].filter(Boolean).join(' · ')}</p>
                  )}
                  {entered && <p className="mt-0.5 text-xs text-gray-500">Entered by {entered}</p>}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <span className="font-medium tabular-nums">{amountText(e.amount, e.unit)}</span>
                  {canEdit(e) && (
                    <span className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => setEditing({ row: e, deleting: false })}
                        className="min-h-[44px] min-w-[44px] rounded px-2 text-[13px] font-semibold text-primary-600 hover:bg-primary-50"
                        aria-label={`Edit ${e.product_name || 'entry'} ${amountText(e.amount, e.unit)}`}
                      >
                        Edit
                      </button>
                      {isSupervisorOrAbove && (
                        <button
                          type="button"
                          onClick={() => setEditing({ row: e, deleting: true })}
                          className="min-h-[44px] min-w-[44px] rounded px-2 text-[13px] font-semibold text-red-700 hover:bg-red-50"
                          aria-label={`Delete ${e.product_name || 'entry'} ${amountText(e.amount, e.unit)}`}
                        >
                          Delete
                        </button>
                      )}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function BundleSection({ farmId, crop, pondAreaHa }: { farmId: string; crop: Crop; pondAreaHa?: number | string | null }) {
  const bundles = useApplicationBundles(farmId);
  if (bundles.length === 0) return null;
  return (
    <Section labelledBy="ap-bundle" title="Apply a bundle" className="border-primary-300">
      <BundleApply farmId={farmId} cropId={crop.id} date={todayInPH()} areaHa={pondAreaHa} />
    </Section>
  );
}

const SUGGESTED = 5;
const inputCls =
  'min-h-[40px] w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20';

/** Out-of-range readings around the chosen date, as reasons a technician can point an application at. */
export function LogApplicationForm({ farmId, crop, onSaved }: { farmId: string; crop: Crop; onSaved?: () => void }) {
  const queryClient = useQueryClient();
  const { data: products = [], isLoading } = useFarmProducts(farmId);
  const [productId, setProductId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(todayInPH);
  const readingReasons = useReadingReasons(farmId, crop.id, date);
  const [reasonPick, setReasonPick] = useState<string>('');
  const [reasonText, setReasonText] = useState('');
  const [notes, setNotes] = useState('');
  const [bigOk, setBigOk] = useState<number | null>(null);
  const journal = useApplicationJournal(farmId, crop.id, {});
  const idempotency = useIdempotencyKey();

  const active = products.filter((p) => p.is_active);
  const product = active.find((p) => p.id === productId) ?? null;
  const suggested = active.slice(0, SUGGESTED);
  const tiles = showAll ? active : product && !suggested.includes(product) ? [...suggested, product] : suggested;
  const reason = reasonPick === 'other' ? reasonText.trim() : reasonPick;
  // "2,5" is 2.5; anything that is not a clean number says so instead of silently
  // leaving Save disabled.
  const parsedAmount = parseTypedAmount(amount);
  const amountNum = 'value' in parsedAmount && parsedAmount.value !== null ? parsedAmount.value : 0;
  const amountError =
    'error' in parsedAmount
      ? parsedAmount.error
      : parsedAmount.value !== null && !(parsedAmount.value > 0)
        ? 'Amount must be more than 0'
        : null;
  const valid = !!product && amountNum > 0 && !amountError;
  // A form with typed content is not saved anywhere: leaving the page or switching tab asks first.
  const unsaved = amount.trim() !== '' || notes.trim() !== '';
  useBlocker({
    shouldBlockFn: () => !window.confirm('Not saved yet. Leave anyway?'),
    disabled: !unsaved,
    enableBeforeUnload: unsaved,
  });
  const priorMax = (journal.data?.pages ?? [])
    .flatMap((pg) => pg.data ?? [])
    .filter((a) => product && a.product_id === product.id)
    .reduce((m, a) => Math.max(m, Number(a.amount) || 0), 0);
  const tooBig = !!product && amountNum > bigAmountLimit(priorMax, product.unit);
  const needsConfirm = valid && tooBig && bigOk !== amountNum;

  const save = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/farms/${farmId}/applications`, {
        cycle_id: crop.id,
        product_id: product!.id,
        amount: amountNum,
        application_date: date,
        reason: reason || null,
        notes: notes.trim() || null,
        idempotency_key: idempotency.keyFor(JSON.stringify([crop.id, product!.id, amountNum, date, reason, notes.trim()])),
      }),
    onSuccess: () => {
      idempotency.reset();
      toast.success(`Logged ${product!.name} ${amountText(amountNum, product!.unit)}`);
      setAmount('');
      setNotes('');
      setReasonPick('');
      setReasonText('');
      queryClient.invalidateQueries({ queryKey: qk.applications.crop(farmId, crop.id) });
      queryClient.invalidateQueries({ queryKey: qk.products.list(farmId) });
      onSaved?.();
    },
  });

  return (
    <Section labelledBy="ap-log" title="Log application" className="border-primary-300">
      <form
        className="flex flex-col gap-3.5"
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid) return;
          if (needsConfirm) {
            setBigOk(amountNum);
            return;
          }
          save.mutate();
        }}
      >
        <fieldset className="flex flex-col gap-1.5">
          <legend className="mb-1.5 text-[13px] font-semibold text-gray-700">Product</legend>
          {isLoading ? (
            <EmptyNote>Loading products…</EmptyNote>
          ) : active.length === 0 ? (
            <EmptyNote>No products yet. A Farm Technician adds them in Settings › Products.</EmptyNote>
          ) : (
            <div className="grid grid-cols-2 gap-1.5">
              {tiles.map((p) => (
                <ProductTile key={p.id} product={p} on={p.id === productId} onPick={() => setProductId(p.id)} />
              ))}
              {active.length > SUGGESTED && (
                <button
                  type="button"
                  onClick={() => setShowAll((s) => !s)}
                  className="min-h-[40px] rounded-lg border border-dashed border-gray-300 px-2.5 text-left text-[13px] font-medium text-gray-600 hover:bg-gray-50"
                >
                  {showAll ? 'Fewer products' : `More products… (${active.length - SUGGESTED})`}
                </button>
              )}
            </div>
          )}
          <span className="text-xs text-gray-500">Recent first. Names and units come from the farm's product list.</span>
        </fieldset>

        <div className="grid grid-cols-2 gap-2.5">
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-gray-700">
            Amount{product ? ` (${product.unit})` : ''}
            <input
              className={cn(inputCls, 'tabular-nums')}
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={product ? `in ${product.unit}` : 'pick a product'}
              aria-invalid={!!amountError}
              aria-describedby={amountError ? 'ap-amount-error' : undefined}
            />
            {amountError && (
              <span id="ap-amount-error" role="alert" className="text-xs font-normal text-red-600">
                {amountError}
              </span>
            )}
          </label>
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-gray-700">
            When
            <input
              type="date"
              className={inputCls}
              value={date}
              max={todayInPH()}
              onChange={(e) => {
                setDate(e.target.value);
                // A reading reason belongs to the old date's readings; a hidden stale pick would still be saved.
                if (reasonPick !== 'Routine' && reasonPick !== 'other') setReasonPick('');
              }}
            />
          </label>
        </div>

        <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-gray-700">
          Reason
          <select className={inputCls} value={reasonPick} onChange={(e) => setReasonPick(e.target.value)}>
            <option value="">No reason given</option>
            {readingReasons.map((r) => (
              <option key={r} value={r}>
                Linked to reading: {r}
              </option>
            ))}
            <option value="Routine">Routine</option>
            <option value="other">Other…</option>
          </select>
        </label>
        {reasonPick === 'other' && (
          <input className={inputCls} value={reasonText} onChange={(e) => setReasonText(e.target.value)} placeholder="What for?" aria-label="Other reason" />
        )}

        <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-gray-700">
          Notes
          <input className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
        </label>

        {bigOk !== null && bigOk === amountNum && tooBig && !save.isPending && (
          <p role="alert" className="text-sm text-amber-800">
            {amountText(amountNum, product?.unit)} is a lot for this product. Press Save again if it is right.
          </p>
        )}
        {save.isError && (
          <p role="alert" className="text-sm text-red-700">
            Not saved: {save.error instanceof Error ? save.error.message : 'try again'}
          </p>
        )}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!valid || save.isPending}
            className="min-h-[40px] rounded-lg bg-primary-600 px-4 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Section>
  );
}

function ProductTile({ product, on, onPick }: { product: FarmProductResponse; on: boolean; onPick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onPick}
      className={cn(
        'flex min-h-[40px] flex-col items-start justify-center rounded-lg border px-2.5 py-1.5 text-left text-[13px]',
        on ? 'border-primary-600 bg-primary-50 font-semibold text-primary-700' : 'border-gray-300 bg-white text-gray-900 hover:bg-gray-50'
      )}
    >
      <span className="w-full truncate">{product.name}</span>
      <span className={cn('text-[11px] font-normal', on ? 'text-primary-600' : 'text-gray-500')}>
        {categoryLabel(product.category)} · {product.unit}
      </span>
    </button>
  );
}

function CropTotals({ farmId, crop }: { farmId: string; crop: Crop }) {
  const { data: summary, isLoading } = useApplicationSummary(farmId, crop.id);
  const products = summary?.products ?? [];
  return (
    <Section labelledBy="ap-totals" title="This crop so far" action={<Kicker>Qty · cost (later)</Kicker>}>
      {isLoading ? (
        <EmptyNote>Loading…</EmptyNote>
      ) : products.length === 0 ? (
        <EmptyNote>Nothing applied yet.</EmptyNote>
      ) : (
        <table className="w-full border-collapse text-sm">
          <tbody>
            {products.map((p) => (
              <tr key={`${p.product_id ?? p.product_name}|${p.unit ?? ''}`} className="border-b border-gray-50">
                <td className="py-1.5 pr-2">
                  {p.product_name}
                  {p.prep_entries ? <span className="ml-1.5 text-xs text-gray-500">{p.prep_entries} in prep</span> : null}
                </td>
                <td className="py-1.5 text-right tabular-nums">{amountText(p.total_amount, p.unit)}</td>
                <td className="py-1.5 pl-3 text-right text-gray-400">later</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {summary && (
        <p className="text-xs text-gray-500">
          {summary.entries} {summary.entries === 1 ? 'entry' : 'entries'}
          {summary.prep_entries ? ` · ${summary.prep_entries} in pre-stocking` : ''}. Costs fill in once prices exist.
        </p>
      )}
    </Section>
  );
}
